import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { API_URL } from "../../config";
import { ToolCall } from "../../multimodal-live-types";
import {
  APP_NAME,
  normalizeTeachInBrief,
  EXPERIENCES,
  FacilitatorPlan,
  FacilitatorReport,
  FacilitatorSessionMetrics,
  TEACH_IN_BRIEF_STORAGE_KEY,
  TEACH_IN_REPORT_STORAGE_KEY,
  TeachInBrief,
  createTeachInFacilitatorConfig,
  facilitatorPlanDeclaration,
} from "../../config/earthDayExperience";
import "./interview.scss";

type ReportResponse = {
  status: "success";
  report: FacilitatorReport;
};

const toStringList = (value: unknown) => {
  if (Array.isArray(value)) {
    return value
      .filter((item): item is string => typeof item === "string")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split(/[\r\n;•]+|,\s+/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const normalizeFacilitatorPlan = (value: unknown): FacilitatorPlan | null => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const candidate = value as Record<string, unknown>;
  const normalizedPlan: FacilitatorPlan = {
    summary:
      typeof candidate.summary === "string" ? candidate.summary.trim() : "",
    learning_objectives: toStringList(candidate.learning_objectives),
    agenda: toStringList(candidate.agenda),
    materials: toStringList(candidate.materials),
    community_actions: toStringList(candidate.community_actions),
  };

  const hasContent =
    normalizedPlan.summary ||
    normalizedPlan.learning_objectives.length ||
    normalizedPlan.agenda.length ||
    normalizedPlan.materials.length ||
    normalizedPlan.community_actions.length;

  return hasContent ? normalizedPlan : null;
};

function InterviewAssistantComponent() {
  const navigate = useNavigate();
  const [brief, setBrief] = useState<TeachInBrief | null>(null);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [plan, setPlan] = useState<FacilitatorPlan | null>(null);
  const [sessionStartedAt, setSessionStartedAt] = useState<string | null>(null);
  const [geminiTurns, setGeminiTurns] = useState(0);
  const [isReturningToBrief, setIsReturningToBrief] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const { client, setConfig, disconnect, connect, connectionStatus } =
    useLiveAPIContext();

  const safePlan = useMemo(() => normalizeFacilitatorPlan(plan), [plan]);

  const sessionStatusMessage =
    connectionStatus === "connecting"
      ? "Connecting to Gemini Live..."
      : connectionStatus === "disconnecting"
        ? "Ending the live planning session..."
        : isGeneratingReport
          ? "Generating facilitator report..."
          : isSessionActive && connectionStatus === "live"
            ? "Live planning is active."
            : isSessionActive
              ? "Preparing the live session..."
            : null;

  const handleReturnToBrief = useCallback(async () => {
    setIsReturningToBrief(true);

    try {
      await disconnect();
    } finally {
      navigate("/facilitator");
    }
  }, [disconnect, navigate]);

  const handleStartSession = useCallback(async () => {
    if (!brief) {
      return;
    }

    setReportError(null);
    setPlan(null);
    setSessionStartedAt(new Date().toISOString());
    setGeminiTurns(0);
    setIsSessionActive(true);

    if (connectionStatus === "idle") {
      try {
        await connect();
      } catch {
        setIsSessionActive(false);
        setReportError(
          "Gemini could not start the live session. Check your connection and API key, then try again.",
        );
      }
    }
  }, [brief, connect, connectionStatus]);

  const handleGenerateReport = useCallback(async () => {
    if (!brief) {
      return;
    }

    setReportError(null);
    setIsSessionActive(false);
    setIsGeneratingReport(true);

    try {
      await disconnect();
    } catch {
      // Keep report generation going even if the socket is already closed.
    }

    const endedAt = new Date().toISOString();

    try {
      const endedAtMs = new Date(endedAt).getTime();
      const startedAtMs = sessionStartedAt
        ? new Date(sessionStartedAt).getTime()
        : endedAtMs;
      const sessionMetrics: FacilitatorSessionMetrics = {
        duration_seconds: Math.max(
          0,
          Math.round((endedAtMs - startedAtMs) / 1000),
        ),
        user_turns: 0,
        gemini_turns: geminiTurns,
        total_turns: geminiTurns,
      };

      const response = await fetch(`${API_URL}/api/facilitator/report`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          brief,
          transcript: [],
          facilitator_plan: safePlan,
          session_metadata: {
            started_at: sessionStartedAt,
            ended_at: endedAt,
            ...sessionMetrics,
          },
        }),
      });

      const data = (await response.json()) as
        | ReportResponse
        | { message?: string };

      if (!response.ok || !("report" in data)) {
        const errorMessage =
          "message" in data && typeof data.message === "string"
            ? data.message
            : "Failed to generate the facilitator report.";
        throw new Error(errorMessage);
      }

      localStorage.setItem(TEACH_IN_REPORT_STORAGE_KEY, JSON.stringify(data.report));
      navigate("/facilitator/report", { state: { justGenerated: true } });
    } catch (error) {
      setIsGeneratingReport(false);
      setReportError(
        error instanceof Error
          ? error.message
          : "Failed to generate the facilitator report.",
      );
    }
  }, [brief, disconnect, navigate, safePlan, sessionStartedAt, geminiTurns]);

  useEffect(() => {
    try {
      const savedBrief = localStorage.getItem(TEACH_IN_BRIEF_STORAGE_KEY);
      if (!savedBrief) {
        navigate("/facilitator");
        return;
      }

      const parsed = normalizeTeachInBrief(
        JSON.parse(savedBrief) as Partial<TeachInBrief>,
      );
      setBrief(parsed);
      localStorage.setItem(TEACH_IN_BRIEF_STORAGE_KEY, JSON.stringify(parsed));
      setConfig(createTeachInFacilitatorConfig(parsed));
    } catch {
      navigate("/facilitator");
    }
  }, [navigate, setConfig]);

  useEffect(() => {
    const onToolCall = (toolCall: ToolCall) => {
      const facilitatorCall = toolCall.functionCalls.find(
        (functionCall) => functionCall.name === facilitatorPlanDeclaration.name,
      );

      if (facilitatorCall) {
        setPlan(normalizeFacilitatorPlan(facilitatorCall.args));
      }

      if (toolCall.functionCalls.length) {
        setTimeout(
          () =>
            client.sendToolResponse({
              functionResponses: toolCall.functionCalls.map((functionCall) => ({
                response: { output: { success: true } },
                id: functionCall.id,
              })),
            }),
          200,
        );
      }
    };

    client.on("toolcall", onToolCall);

    return () => {
      client.off("toolcall", onToolCall);
    };
  }, [client]);

  useEffect(() => {
    if (!isSessionActive) {
      return;
    }

    const onTurnComplete = () => {
      setGeminiTurns((current) => current + 1);
    };

    client.on("turncomplete", onTurnComplete);

    return () => {
      client.off("turncomplete", onTurnComplete);
    };
  }, [client, isSessionActive]);

  if (!brief) {
    return null;
  }

  return (
    <div className="facilitator-shell">
      <section className="facilitator-session-card">
        <div className="session-card-copy">
          <span className="facilitator-kicker">
            {EXPERIENCES.facilitator.eyebrow}
          </span>
          <h1>{EXPERIENCES.facilitator.title}</h1>
          <p>
            Use the live Gemini controls below to talk through your session.
            For stability, live transcript capture is currently disabled. When
            you are ready, {APP_NAME} will turn your brief and structured plan
            into a coaching report you can review and download as a PDF.
          </p>
        </div>

        <div className="session-brief-card">
          <div>
            <span className="brief-label">Session</span>
            <strong>{brief.sessionTitle}</strong>
          </div>
          <div>
            <span className="brief-label">Audience</span>
            <strong>{brief.audience}</strong>
          </div>
          <div>
            <span className="brief-label">Context</span>
            <strong>{brief.venueContext || "Not specified"}</strong>
          </div>
          <div>
            <span className="brief-label">Duration</span>
            <strong>{brief.duration || "Flexible"}</strong>
          </div>
        </div>

        <div className="session-actions">
          {!isSessionActive ? (
            <button
              onClick={() => {
                void handleStartSession();
              }}
              disabled={isGeneratingReport || isReturningToBrief}
              className="facilitator-primary"
            >
              Start live planning
            </button>
          ) : (
            <button
              onClick={() => {
                void handleGenerateReport();
              }}
              disabled={isGeneratingReport}
              className="facilitator-primary warm"
            >
              End planning and generate report
            </button>
          )}

          <button
            onClick={() => {
              void handleReturnToBrief();
            }}
            disabled={isReturningToBrief || isGeneratingReport}
            className="facilitator-secondary"
          >
            Back to brief
          </button>
        </div>

        {sessionStatusMessage && (
          <div
            className={`session-status ${
              isGeneratingReport || connectionStatus === "disconnecting"
                ? "muted"
                : ""
            }`}
          >
            {sessionStatusMessage}
          </div>
        )}

        {reportError && <div className="session-status error">{reportError}</div>}
      </section>

      {safePlan && (
        <section className="facilitator-plan-card">
          <div className="plan-header">
            <span>Structured session brief</span>
            <h2>Earth Day teach-in plan</h2>
          </div>

          <div className="plan-summary">{safePlan.summary}</div>

          <div className="plan-grid">
            <div className="plan-panel">
              <h3>Learning objectives</h3>
              <ul>
                {safePlan.learning_objectives.map((item, index) => (
                  <li key={`${item}-${index}`}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="plan-panel">
              <h3>Agenda</h3>
              <ol>
                {safePlan.agenda.map((item, index) => (
                  <li key={`${item}-${index}`}>{item}</li>
                ))}
              </ol>
            </div>

            <div className="plan-panel">
              <h3>Materials</h3>
              <ul>
                {safePlan.materials.map((item, index) => (
                  <li key={`${item}-${index}`}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="plan-panel">
              <h3>Community actions</h3>
              <ul>
                {safePlan.community_actions.map((item, index) => (
                  <li key={`${item}-${index}`}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export const InterviewAssistant = memo(InterviewAssistantComponent);
